import {useCallback, useEffect, useRef, useState} from 'react';

import {pinResetRequestService} from '@/services/pinResetRequestService';
import type {
  AdminPinResetDecisionPayload,
  AdminPinResetRequest,
  AdminPinResetRequestListResponse,
} from '@/types';
import {BusinessPinResetRequestStatus} from '@/types';

const PAGE_LIMIT = 10;

/**
 * The operations queue. It opens on the pending requests, because acting on one
 * is the only thing the screen exists for; the other statuses stay one filter
 * away so a decision can be looked up afterwards.
 */
export const usePinResetRequests = () => {
  const [list, setList] = useState<AdminPinResetRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeciding, setIsDeciding] = useState(false);
  // Set when the last load failed, so the screen can tell a failure apart from
  // an empty queue. Cleared as soon as the next load starts.
  const [loadFailed, setLoadFailed] = useState(false);
  const [status, setStatus] = useState<BusinessPinResetRequestStatus>(
    BusinessPinResetRequestStatus.Pending,
  );
  const [displayPage, setDisplayPage] = useState(1);
  const [isPageReset, setIsPageReset] = useState(false);
  const currentPageRef = useRef(0);

  const applyList = useCallback((data?: AdminPinResetRequestListResponse) => {
    setList(data?.data ?? []);
    setTotal(data?.total ?? 0);
    setIsLoading(false);
  }, []);

  const failLoad = useCallback(() => {
    setList([]);
    setTotal(0);
    setLoadFailed(true);
    setIsLoading(false);
  }, []);

  const fetchList = useCallback(
    async (params?: {page?: number; limit?: number}) => {
      if (params?.page !== undefined) {
        currentPageRef.current = params.page;
        setDisplayPage(params.page + 1);
      }
      setIsLoading(true);
      setLoadFailed(false);
      try {
        applyList(
          await pinResetRequestService.getList({
            page: params?.page ?? currentPageRef.current,
            limit: params?.limit ?? PAGE_LIMIT,
            status,
          }),
        );
      } catch {
        failLoad();
      }
    },
    [status, applyList, failLoad],
  );

  // Loads the first page on mount and on every status change. State is only
  // set once the read settles; `selectStatus` resets the page and the loading
  // flags beforehand.
  useEffect(() => {
    pinResetRequestService
      .getList({page: 0, limit: PAGE_LIMIT, status})
      .then(applyList)
      .catch(failLoad);
  }, [status, applyList, failLoad]);

  const selectStatus = useCallback((next: BusinessPinResetRequestStatus) => {
    currentPageRef.current = 0;
    setDisplayPage(1);
    setIsLoading(true);
    setLoadFailed(false);
    setStatus(next);
    setIsPageReset((previous) => !previous);
  }, []);

  const decide = useCallback(
    async (requestId: string, payload: AdminPinResetDecisionPayload) => {
      setIsDeciding(true);
      try {
        const decided = await pinResetRequestService.decide(requestId, payload);
        // A decided request leaves the pending queue, so re-read rather than
        // patching the row in place: the total and the remaining rows change.
        await fetchList({page: 0});
        return decided;
      } finally {
        setIsDeciding(false);
      }
    },
    [fetchList],
  );

  return {
    list,
    total,
    isLoading,
    isDeciding,
    loadFailed,
    status,
    displayPage,
    isPageReset,
    fetchList,
    selectStatus,
    decide,
  };
};
